import React from 'react';
import { motion } from 'framer-motion';
import { Code2, Smartphone, Database, MousePointer2 } from 'lucide-react';
import clsx from 'clsx';

// --- COMPONENTS ---

const NodeLabel = ({ icon: Icon, label, className, delay = 0 }: any) => (
  <motion.div 
    className={clsx(
      "absolute z-20 flex flex-col items-center",
      className
    )}
    initial={{ opacity: 0, scale: 0.9, y: 10 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ 
      opacity: { delay, duration: 0.6 },
      scale: { delay, duration: 0.6 },
      y: { duration: 4, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" } 
    }}
  >
    {/* The Label Box */}
    <div className={clsx(
      "flex items-center gap-2 bg-slate-900/95 backdrop-blur-md border border-slate-700 shadow-[4px_4px_0_0_rgba(0,0,0,0.5)] rounded-lg px-3 py-2",
      "relative z-10"
    )}>
      <div className="bg-slate-800 p-1 rounded-md border border-slate-700">
        <Icon size={14} className="text-emerald-400" />
      </div>
      <span className="text-[10px] md:text-xs font-bold uppercase tracking-wider text-slate-100 whitespace-nowrap">
        {label}
      </span>
    </div>

    {/* The Anchor Line/Dot - Visual Connector */}
    <div className="relative flex flex-col items-center -mt-1 pointer-events-none">
       <div className="w-0.5 h-4 bg-gradient-to-b from-slate-700 to-emerald-500/50"></div>
       <div className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.8)] animate-pulse"></div>
    </div>
  </motion.div>
);

const Cursor = ({ label, color, x, y, xMove, yMove, delay }: any) => (
  <motion.div 
    className="absolute z-30 pointer-events-none drop-shadow-xl"
    style={{ top: y, left: x }}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1, x: xMove, y: yMove }}
    transition={{ 
      opacity: { delay, duration: 0.3 },
      x: { delay: delay + 0.5, duration: 4, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" },
      y: { delay: delay + 0.5, duration: 5, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }
    }}
  >
    <MousePointer2 className={clsx("w-6 h-6 stroke-[1.5px] stroke-slate-900", color)} fill="currentColor" />
    <div className={clsx(
      "ml-4 -mt-1 px-2.5 py-1 rounded-md text-[10px] font-bold text-slate-900 shadow-lg whitespace-nowrap", 
      color.replace('text-', 'bg-')
    )}>
      {label}
    </div>
  </motion.div>
);

// --- MAIN VISUAL ---

export const HeroVisual = () => {
  return (
    <div className="relative w-full aspect-square max-w-[650px] mx-auto perspective-1000">
      
      {/* 1. AMBIENT GLOW BEHIND */}
      <div className="absolute inset-0 bg-emerald-500/15 rounded-full blur-[80px] -z-10" />

      {/* 2. THE MOTHERBOARD CONTAINER */}
      <motion.div 
        className="w-full h-full bg-[#0B0F19] border-2 border-slate-900 rounded-3xl overflow-hidden shadow-2xl relative"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* ILLUSTRATION */}
        <img 
          src="/images/illustrations/canvas-os-motherboard.avif" 
          alt="Canvas OS Operational Architecture" 
          className="w-full h-full object-cover scale-[1.02]"
        />

        {/* --- STATIC LABELS (The Infrastructure) --- */}
        
        {/* 1. Knowledge Graph -> Points to the Funnel (Top Left Quadrant) */}
        <NodeLabel 
          icon={Database} 
          label="Knowledge Graph" 
          className="top-[33%] left-[3%]" 
          delay={0.5} 
        />
        
        {/* 2. Code Agent -> Points to the VS Code Screen (Top Right Quadrant) */}
        <NodeLabel 
          icon={Code2} 
          label="Code Agent" 
          className="top-[21%] right-[33%]" 
          delay={0.7} 
        />
        
        {/* 3. Preview -> Points to the Dashboard Tablet (Bottom Right Quadrant) */}
        <NodeLabel 
          icon={Smartphone} 
          label="Preview" 
          className="bottom-[17%] right-[1%]" 
          delay={0.9} 
        />

        {/* --- CURSORS (The Multiplayer Workflow) --- */}
        
        {/* 1. AI Architect (Blue) -> Working on the Network Graph (Bottom Left) */}
        <Cursor 
          label="AI Architect" 
          color="text-indigo-400" 
          x="32%" y="58%" 
          xMove={[-8, 8]} 
          yMove={[-3, 3]} 
          delay={1.2} 
        />

        {/* 2. Alex Dev (Yellow) -> Reviewing the Code Screen (Top Right) */}
        <Cursor 
          label="Alex (Dev)" 
          color="text-amber-400" 
          x="74%" y="30%" 
          xMove={[5, -5]} 
          yMove={[5, -5]} 
          delay={1.5} 
        />

        {/* 3. You (Green) -> Hitting the "DEPLOY" Button (Center) */}
        <Cursor 
          label="You (Approving)" 
          color="text-emerald-400" 
          x="50%" y="48%" 
          xMove={[2, -2]} 
          yMove={[-2, 2]} 
          delay={1.8} 
        />

        {/* Status Badge - Bottom Left Corner */}
        <div className="absolute bottom-6 left-6 z-20">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-black/80 backdrop-blur rounded-full border border-slate-800 shadow-lg">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[10px] font-mono text-emerald-500 font-bold tracking-widest">
                    CANVAS_OS :: ONLINE
                </span>
            </div>
        </div>

      </motion.div>
    </div>
  );
};